// Page: info-form  ("My Info" step — collect name/address/phone, then branch
// into the LMI / usage / consent flow).
//
// Ported from the inline <script> in
//   Enrollment_modules/info/form.module/module.html (lines 250–3450).
//
// MECHANICAL / FAITHFUL PORT — runtime behaviour is preserved. Deliberate changes
// (same family as the reference ports; see docs/handoff/global-js-deep-refactor-notes.md):
//   1. Selectors scoped to this module's HubSpot render-time wrapper
//      `#page_1_my_info_widget #child` are now scoped to `root` (via the q/qi
//      helpers). Whole-widget toggles (`getElementById('page_1_my_info_widget')`),
//      the sibling LMI widget (`page_1_lmi_widget`, `#Income_Exceed_Threshold`,
//      `.lmifieldswrapper`, …), page-global nodes (`body`, `button`, `a`,
//      overlays, `#date`, `.county-Wrapper`) stay document-scoped, as in the
//      original.
//   2. `_hcms/api/*` fetches are ROOT-RELATIVE (`apiUrl`) and same-origin
//      redirects are root-relative paths (`/`, `/my-usage`, `/myusage`) instead
//      of hardcoding https://enrollment.nautilussolar.com.
//   3. HubDB reads go through core/hubdb (`hubdbRowsUrl`) using window.__ENV__
//      table ids + fixed dataPortalId instead of inline literals.
//   4. localStorage goes through core/storage (rawGet/rawSet — the many loosely
//      named keys aren't in the typed StorageKey union yet).
//   5. The Census-Tract check still POSTs to the external Hansen host
//      (hansen.danmarcrm.com) unchanged — it is not same-origin.
//
// Dynamic ES5 patterns (HubDB row shapes, vars reassigned across types) are typed
// `any` to keep the port mechanical. The large commented-out "Multiple enrollment"
// block from the original is dead code and was dropped.

import { apiUrl } from "../../core/api"
import { hubdbRowsUrl } from "../../core/hubdb"
import * as storage from "../../core/storage"

export function init(root: HTMLElement, _config: unknown): void {
__ea_trace("modules/shared/info-form.ts","init",[root,_config],["root","_config"]);
  // ─── Scoped DOM helpers ──────────────────────────────────────────────────────
  const q = (sel: string) => root.querySelector(sel) as HTMLElement
  const qi = (sel: string) => root.querySelector(sel) as HTMLInputElement
  const dq = (sel: string) => document.querySelector(sel) as HTMLElement
  const di = (id: string) => document.getElementById(id) as HTMLElement
  const dii = (id: string) => document.getElementById(id) as HTMLInputElement

  // implicit globals in the original (bare assigns, no `var`) — declared here so
  // strict-mode ESM doesn't throw ReferenceError.
  let responseFromAPI: any = ""
  let exampleUtilityId: any
  let incomefileUploadControl: any

  /* loader function */
  function myInfoopenLoader() {
__ea_trace("modules/shared/info-form.ts","myInfoopenLoader",[],[]);
    const loader = di("myInfoLoader")
    const buttons = document.querySelectorAll("button")
    const anchors = document.querySelectorAll("a")
    di("MIloaderOverlay").style.display = "block"
    loader.style.display = "flex"
    buttons.forEach((button) => {
__ea_trace("modules/shared/info-form.ts","(anon)",[button],["button"]);
      button.disabled = true
    })
    anchors.forEach((anchor) => {
__ea_trace("modules/shared/info-form.ts","(anon)",[anchor],["anchor"]);
      ;(anchor as any).disabled = true
    })
  }
  function myInfocloseLoader() {
__ea_trace("modules/shared/info-form.ts","myInfocloseLoader",[],[]);
    const loader = di("myInfoLoader")
    const buttons = document.querySelectorAll("button")
    const anchors = document.querySelectorAll("a")
    di("MIloaderOverlay").style.display = "none"
    loader.style.display = "none"
    buttons.forEach((button) => {
__ea_trace("modules/shared/info-form.ts","(anon)",[button],["button"]);
      button.disabled = false
    })
    anchors.forEach((anchor) => {
__ea_trace("modules/shared/info-form.ts","(anon)",[anchor],["anchor"]);
      ;(anchor as any).disabled = false
    })
  }
  /* loader function */
  async function delay(ms: number) {
__ea_trace("modules/shared/info-form.ts","delay",[ms],["ms"]);
    return new Promise((resolve) => setTimeout(resolve, ms))
  }
  void delay

  const ls_isWeBackFromME = storage.rawGet("isWeBackinMultiEnroll")
  const ms_currentUrl = window.location.href
  void ms_currentUrl

  /* -------------------------------------- Start Single enrollemnt code ------------------------------------- */
  let isMobile: any
  window.onload = () => {
__ea_trace("modules/shared/info-form.ts","(anon)",[],[]);
    const dateInput = di("date")
    if (window.innerWidth <= 1024) {
      // On mobile, prevent default behavior of the input
      dateInput.setAttribute("type", "text")
      q("#date").addEventListener("input", validateDateMobile)
    } else {
      // On desktop, enable manual input by setting a placeholder
      dateInput.setAttribute("type", "date") // Change input type to date
      dateInput.focus() // Open the date picker
      dateInput.blur()
      q("#date").addEventListener("input", validateDate)
    }
  }

  // If user is not logedIn then redirected them to login page
  function getEmail() {
__ea_trace("modules/shared/info-form.ts","getEmail",[],[]);
    const storedEmail = storage.rawGet("userEmail")
    if (!storedEmail) {
      window.location.href = "/"
      return false
    }
    getContactRecord(storedEmail)
  }
  getEmail()

  q(".firstName-Wrapper").style.display = "block"
  q(".lastName-Wrapper").style.display = "block"
  q(".date-Wrapper").style.display = "block"
  q(".homePhone-Wrapper").style.display = "block"
  q(".cellPhone-Wrapper").style.display = "block"
  q(".check-title").style.display = "block"
  q(".check-info").style.display = "block"
  q(".unsubscibeCheckbox").style.display = "block"

  // validation code
  q("#first_name").addEventListener("input", validateFirstName)
  q("#last_name").addEventListener("input", validateLastName)
  q("#address1").addEventListener("input", validateAddress1)
  q("#city").addEventListener("input", validateCity)
  q("#state").addEventListener("change", validateState)
  q("#county").addEventListener("input", validateCounty)
  q("#home_phone").addEventListener("input", validateHomePhone)
  q("#cell_phone").addEventListener("input", validateCellPhone)

  function validateFirstName() {
__ea_trace("modules/shared/info-form.ts","validateFirstName",[],[]);
    q("#allfield_error_message").innerText = ""
    const firstName = qi("#first_name").value.trim()
    if (firstName === "") {
      q("#first_name_error_message").innerHTML = "Please enter your First Name."
    } else if (!/^[a-zA-Z ]+$/.test(firstName)) {
      q("#first_name_error_message").innerHTML = "Please enter a valid First Name with only alphabetic characters."
    } else {
      q("#first_name_error_message").innerHTML = ""
    }
  }
  function validateLastName() {
__ea_trace("modules/shared/info-form.ts","validateLastName",[],[]);
    q("#allfield_error_message").innerText = ""
    const lastName = qi("#last_name").value.trim()
    if (lastName === "") {
      q("#last_name_error_message").innerHTML = "Please enter your Last Name."
    } else if (!/^[a-zA-Z ]+$/.test(lastName)) {
      q("#last_name_error_message").innerHTML = "Please enter a valid Last Name with only alphabetic characters."
    } else {
      q("#last_name_error_message").innerHTML = ""
    }
  }
  function validateDate() {
__ea_trace("modules/shared/info-form.ts","validateDate",[],[]);
    q("#allfield_error_message").innerText = ""
    const birthdateInput = qi("#date")
    const birthdate = birthdateInput.value.trim()
    if (birthdate === "") {
      q("#date_error_message").innerHTML = "Please enter your Date of Birth."
    } else {
      q("#date_error_message").innerHTML = ""
      const today = new Date()
      const birthdateDate = new Date(birthdate)
      const age = today.getFullYear() - birthdateDate.getFullYear()
      if (age < 18) {
        q("#date_error_message").innerHTML = "You must be 18 years of age or older to enroll."
      } else {
        q("#date_error_message").innerHTML = ""
      }
    }
  }

  function validateDateMobile() {
__ea_trace("modules/shared/info-form.ts","validateDateMobile",[],[]);
    q("#allfield_error_message").innerText = ""
    const dateInput = dii("date")
    const inputValue = dateInput.value.trim()
    let sanitizedValue = inputValue.replace(/[^\d]/g, "")
    if (sanitizedValue.length > 2) {
      sanitizedValue = `${sanitizedValue.slice(0, 2)}/${sanitizedValue.slice(2)}`
    }
    if (sanitizedValue.length > 5) {
      sanitizedValue = `${sanitizedValue.slice(0, 5)}/${sanitizedValue.slice(5, 9)}`
    }
    dateInput.value = sanitizedValue
    const dateRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/
    if (inputValue === "") {
      q("#date_error_message").innerHTML = "Please enter your Date of Birth."
    } else if (!dateRegex.test(sanitizedValue)) {
      q("#date_error_message").innerHTML = "Please enter a date in the format mm/dd/yyyy"
    } else {
      q("#date_error_message").innerHTML = ""
      const birthdateInput = dii("date")
      const birthdate = birthdateInput.value.trim()
      const today = new Date()
      const birthdateDate = new Date(birthdate)
      const age = today.getFullYear() - birthdateDate.getFullYear()
      if (age < 18) {
        q("#date_error_message").innerHTML = "You must be 18 years of age or older to enroll."
      } else {
        q("#date_error_message").innerHTML = ""
      }
    }
  }
  function validateAddress1() {
__ea_trace("modules/shared/info-form.ts","validateAddress1",[],[]);
    q("#allfield_error_message").innerText = ""
    const address1 = qi("#address1").value.trim()
    if (address1 === "") {
      q("#address1_error_message").innerHTML = "Please enter your Street Address."
    } else {
      q("#address1_error_message").innerHTML = ""
    }
  }
  function validateAddress2() {
__ea_trace("modules/shared/info-form.ts","validateAddress2",[],[]);
    q("#allfield_error_message").innerText = ""
    const address2 = qi("#address2").value.trim()
    if (address2 === "") {
      q("#address2_error_message").innerHTML = "Please enter address line 2."
    } else {
      q("#address2_error_message").innerHTML = ""
    }
  }
  void validateAddress2
  function validateCity() {
__ea_trace("modules/shared/info-form.ts","validateCity",[],[]);
    q("#allfield_error_message").innerText = ""
    const city = qi("#city").value.trim()
    if (city === "") {
      q("#city_error_message").innerHTML = "Please enter your City."
    } else if (!/^[a-zA-Z ]+$/.test(city)) {
      q("#city_error_message").innerHTML = "Please enter a valid City with only alphabetic characters."
    } else {
      q("#city_error_message").innerHTML = ""
    }
  }
  function validateState() {
__ea_trace("modules/shared/info-form.ts","validateState",[],[]);
    const state = qi("#state").value
    if (state === "") {
      q("#state_error_message").innerHTML = "Please enter your State."
    } else {
      q("#state_error_message").innerHTML = ""
    }
  }
  function validateCounty() {
__ea_trace("modules/shared/info-form.ts","validateCounty",[],[]);
    q("#allfield_error_message").innerText = ""
    const county = qi("#county").value.trim()
    if (county === "") {
      q("#county_error_message").innerHTML = "Please enter county."
    } else if (!/^[a-zA-Z ]+$/.test(county)) {
      q("#county_error_message").innerHTML = "Please enter a valid county with only alphabetic characters."
    } else {
      q("#county_error_message").innerHTML = ""
    }
  }

  function validateHomePhone() {
__ea_trace("modules/shared/info-form.ts","validateHomePhone",[],[]);
    q("#allfield_error_message").innerText = ""
    let phoneNumber = qi("#home_phone").value.trim()
    phoneNumber = phoneNumber.replace(/\D/g, "")
    let CellphoneNumber = qi("#cell_phone").value.trim()
    CellphoneNumber = CellphoneNumber.replace(/\D/g, "")
    if (CellphoneNumber === "" || !/^\d{10}$/.test(CellphoneNumber)) {
      q("#cell_phone_error_message").innerHTML = ""
      if (phoneNumber === "") {
        q("#home_phone_error_message").innerHTML = "Please enter at least one of Mobile or Home Phone."
      } else if (!/^\d{10}$/.test(phoneNumber)) {
        q("#home_phone_error_message").innerHTML = "Please enter a valid 10-digit phone number."
      } else {
        q("#home_phone_error_message").innerHTML = ""
      }
    } else {
      if (phoneNumber === "") {
        q("#home_phone_error_message").innerHTML = ""
      } else if (!/^\d{10}$/.test(phoneNumber)) {
        q("#home_phone_error_message").innerHTML = "Please enter a valid 10-digit phone number."
      } else {
        const a = qi("#phoneCheckBox")
        void a
      }
    }

    if (phoneNumber.length <= 3) {
      qi("#home_phone").value = phoneNumber // No formatting needed yet
    } else if (phoneNumber.length <= 6) {
      qi("#home_phone").value = phoneNumber.replace(/(\d{3})(\d{1,3})/, "$1-$2")
    } else {
      qi("#home_phone").value = phoneNumber.replace(/(\d{3})(\d{3})(\d{1,4})/, "$1-$2-$3")
    }
  }
  function validateCellPhone() {
__ea_trace("modules/shared/info-form.ts","validateCellPhone",[],[]);
    q("#allfield_error_message").innerText = ""
    let phoneNumber = qi("#cell_phone").value.trim()
    phoneNumber = phoneNumber.replace(/\D/g, "")
    let homephoneNumber = qi("#home_phone").value.trim()
    homephoneNumber = homephoneNumber.replace(/\D/g, "")
    if (homephoneNumber === "" || !/^\d{10}$/.test(homephoneNumber)) {
      q("#home_phone_error_message").innerHTML = ""
      if (phoneNumber === "") {
        q("#cell_phone_error_message").innerHTML = "Please enter at least one of Mobile or Home Phone."
      } else if (!/^\d{10}$/.test(phoneNumber)) {
        q("#cell_phone_error_message").innerHTML = "Please enter a valid 10-digit phone number."
      } else {
        q("#cell_phone_error_message").innerHTML = ""
      }
    } else {
      if (phoneNumber === "") {
        q("#cell_phone_error_message").innerHTML = ""
      } else if (!/^\d{10}$/.test(phoneNumber)) {
        q("#cell_phone_error_message").innerHTML = "Please enter a valid 10-digit phone number."
      } else {
        q("#cell_phone_error_message").innerHTML = ""
      }
    }

    if (phoneNumber.length <= 3) {
      qi("#cell_phone").value = phoneNumber // No formatting needed yet
    } else if (phoneNumber.length <= 6) {
      qi("#cell_phone").value = phoneNumber.replace(/(\d{3})(\d{1,3})/, "$1-$2")
    } else {
      qi("#cell_phone").value = phoneNumber.replace(/(\d{3})(\d{3})(\d{1,4})/, "$1-$2-$3")
    }
  }
  function validateForm() {
__ea_trace("modules/shared/info-form.ts","validateForm",[],[]);
    validateFirstName()
    validateLastName()
    validateDate()
    validateAddress1()
    validateCity()
    validateState()
    const st = storage.rawGet("FarmStateAbbreviation")
    if (st === "MN") {
      validateCounty()
    }
    validateHomePhone()
    validateCellPhone()

    const errorMessages = root.querySelectorAll("#page_1_my_info_widget .error-message") // Check if any error messages are present
    let hasErrors = false
    for (let i = 0; i < errorMessages.length; i++) {
      if (errorMessages[i].innerHTML !== "") {
        hasErrors = true
        break
      }
    }
    if (hasErrors) {
      q("#allfield_error_message").innerText =
        "There are errors in the form above. Please scroll up and correct them to continue."
      return false
    } else {
      q("#allfield_error_message").innerText = ""
      addDataToContactRecord()
    }
  }

  async function convertDateFormatWhenGettingContactRecord(dateValue: string) {
__ea_trace("modules/shared/info-form.ts","convertDateFormatWhenGettingContactRecord",[dateValue],["dateValue"]);
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/
    if (dateRegex.test(dateValue)) {
      const parts = dateValue.split("-")
      const yyyy = parts[0]
      const mm = parts[1]
      const dd = parts[2]
      return `${mm}/${dd}/${yyyy}`
    } else {
      return dateValue
    }
  }
  async function getDealRecord() {
__ea_trace("modules/shared/info-form.ts","getDealRecord",[],[]);
    const dealId = storage.rawGet("dealId")
    let catchdata: any = ""
    try {
      const data = {
        deal_id: dealId,
      }
      const response = await fetch(apiUrl("GetDealRecordBasedOnEmail"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      })
      catchdata = data
      const dataa = await response.json()
      responseFromAPI = JSON.stringify(dataa)
      const street_address_1 = dataa.userValue.properties.street_address
      const street_address_2 = dataa.userValue.properties.street_address_2
      const log_dealOwner = dataa.userValue.properties.hubspot_owner_id
      void log_dealOwner
      const service_city = dataa.userValue.properties.city
      const service_state_abbreviation = dataa.userValue.properties.service_state_abbreviation
      const service_county = dataa.userValue.properties.county
      const Service_postal_code = dataa.userValue.properties.postal_code
      if (street_address_1 != null) {
        qi("#address1").value = street_address_1
      }
      if (street_address_2 != null) {
        qi("#address2").value = street_address_2
      }
      if (service_city != null) {
        qi("#city").value = service_city
      }
      if (service_state_abbreviation != null) {
        if (service_state_abbreviation === "MN") {
          qi("#state").value = "MN"
          dq(".county-Wrapper").style.setProperty("display", "block", "important")
        } else {
          if (storage.rawGet("utility_id") === "XCEL-SF") {
            qi("#state").value = "MN"
            dq(".county-Wrapper").style.setProperty("display", "block", "important")
          } else {
            qi("#state").value = service_state_abbreviation
          }
        }
      }
      if (service_county != null) {
        qi("#county").value = service_county
      }
      if (Service_postal_code != null) {
        qi("#code").value = Service_postal_code
      }
      await addedConfigIdWithSomeDataOnLoad()
    } catch (error) {
      console.error("Error:", error)
    }
  }

  async function getContactRecord(email: string) {
__ea_trace("modules/shared/info-form.ts","getContactRecord",[email],["email"]);
    let catchdata: any = ""
    try {
      const data = {
        email: email,
      }
      const response = await fetch(apiUrl("GetContactRecordBasedOnEmail"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      })
      catchdata = data
      const dataa = await response.json()
      responseFromAPI = JSON.stringify(dataa)
      const firstName = dataa.userValue.properties.firstname
      const lastName = dataa.userValue.properties.lastname
      let birthdateInput = dataa.userValue.properties.date_of_birth
      const homeNumber = dataa.userValue.properties.phone
      const cellNumber = dataa.userValue.properties.mobilephone
      const textConsent = dataa.userValue.properties.text_consent
      const selectedValues: any[] = []
      void selectedValues
      const recordId = dataa.userValue.properties.hs_object_id
      qi("#first_name").value = firstName
      qi("#last_name").value = lastName
      if (birthdateInput != null) {
        if (birthdateInput === "") {
        } else {
          if (isMobile) {
            birthdateInput = await convertDateFormatWhenGettingContactRecord(birthdateInput)
            qi("#date").value = birthdateInput
          } else {
            qi("#date").value = birthdateInput
          }
        }
      } else {
      }
      qi("#home_phone").value = homeNumber
      qi("#cell_phone").value = cellNumber
      storage.rawSet("RecordId", recordId)
      const checkboxess = qi("#phoneCheckBox")
      if (textConsent === "Yes") {
        checkboxess.checked = true
      } else {
        checkboxess.checked = false
      }
      const getuser_device = dataa.userValue.properties.user_device
      const getuser_agentBrowser = dataa.userValue.properties.user_agent
      const getuser_operating_system = dataa.userValue.properties.user_agent___operating_system
      if (getuser_device !== "" || getuser_agentBrowser !== "" || getuser_operating_system !== "") {
        if (getuser_device != null || getuser_agentBrowser != null || getuser_operating_system != null) {
          storage.rawSet("User Device", getuser_device)
          storage.rawSet("Browser Name", getuser_agentBrowser)
          storage.rawSet("Operating System", getuser_operating_system)
        }
      }
      await getDealRecord()
    } catch (error) {
      console.error("Error:", error)
    }
  }

  async function convertDateFormat(dateValue: string) {
__ea_trace("modules/shared/info-form.ts","convertDateFormat",[dateValue],["dateValue"]);
    const dateRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/
    if (dateRegex.test(dateValue)) {
      const parts = dateValue.split("/")
      const mm = parts[0]
      const dd = parts[1]
      const yyyy = parts[2]
      return `${yyyy}-${mm}-${dd}`
    } else {
      return dateValue
    }
  }

  async function addDataToContactRecord() {
__ea_trace("modules/shared/info-form.ts","addDataToContactRecord",[],[]);
    const firstName = qi("#first_name").value.trim()
    const lastName = qi("#last_name").value.trim()
    const birthdateInput = qi("#date")
    let birthdate = birthdateInput.value.trim()
    if (isMobile) {
      birthdate = await convertDateFormat(birthdate)
    }
    const city = qi("#city").value.trim()
    const homeNumber = qi("#home_phone").value.trim()
    const cellNumber = qi("#cell_phone").value.trim()
    const checkboxs = qi("#phoneCheckBox")
    let phoneCheckBox: any
    let emailCheckbox: any
    let textCheckbox: any
    if (homeNumber) {
      phoneCheckBox = "Phone"
      emailCheckbox = "true"
    } else {
      phoneCheckBox = ""
      emailCheckbox = "false"
    }
    if (checkboxs.checked && cellNumber) {
      textCheckbox = "true"
    } else {
      textCheckbox = "false"
    }
    let checkboxesVal: any
    if (checkboxs.checked) {
      checkboxesVal = "Yes"
    } else {
      checkboxesVal = "No"
    }
    const email = storage.rawGet("userEmail")
    let catchdata: any = ""
    try {
      myInfoopenLoader()
      const data = {
        properties: {
          email: email,
          firstname: firstName,
          lastname: lastName,
          date_of_birth: birthdate,
          phone: homeNumber,
          mobilephone: cellNumber,
          contact_preference: phoneCheckBox,
          contact_preference_email: emailCheckbox,
          contact_preference_text: textCheckbox,
          text_consent: checkboxesVal,
        },
      }
      const response = await fetch(apiUrl("UpdateContactRecord"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      })
      catchdata = data
      if (response.ok) {
        storage.rawSet("LoggedName", `${firstName} ${lastName}`)
        storage.rawSet("CompanyName", `${firstName}&${lastName}`)
        storage.rawSet("FirstName", firstName)
        storage.rawSet("LastName", lastName)
        storage.rawSet("LastName", lastName)

        if (homeNumber != null && homeNumber !== undefined) {
          storage.rawSet("HomePhone", homeNumber)
        }
        if (cellNumber != null && cellNumber !== undefined) {
          storage.rawSet("CellPhone", cellNumber)
        }
        storage.rawSet("city", city)
        await getAvailability()
      }

      const result = await response.json()
      responseFromAPI = JSON.stringify(result)
      if (response.status === 500 || response.status === 404) {
        myInfocloseLoader()
      }
    } catch (error) {
      myInfocloseLoader()
      console.error("Error:", error)
    }
  }

  async function saveMyPlace() {
__ea_trace("modules/shared/info-form.ts","saveMyPlace",[],[]);
    const firstName = qi("#first_name").value.trim()
    const lastName = qi("#last_name").value.trim()
    const birthdateInput = qi("#date")
    let birthdate = birthdateInput.value.trim()
    if (isMobile) {
      birthdate = await convertDateFormat(birthdate)
    }
    const homeNumber = qi("#home_phone").value.trim()
    const cellNumber = qi("#cell_phone").value.trim()
    const checkedValues: any[] = []
    void checkedValues
    const checkboxs = qi("#phoneCheckBox")
    let phoneCheckBox: any
    let emailCheckbox: any
    let textCheckbox: any
    if (homeNumber) {
      phoneCheckBox = "Phone"
      emailCheckbox = "true"
    } else {
      phoneCheckBox = ""
      emailCheckbox = "false"
    }
    if (checkboxs.checked && cellNumber) {
      textCheckbox = "true"
    } else {
      textCheckbox = "false"
    }
    let checkboxesVal: any
    if (checkboxs.checked) {
      checkboxesVal = "Yes"
    } else {
      checkboxesVal = "No"
    }
    const email = storage.rawGet("userEmail")
    let catchdata: any = ""
    try {
      const data = {
        properties: {
          email: email,
          firstname: firstName,
          lastname: lastName,
          date_of_birth: birthdate,
          phone: homeNumber,
          mobilephone: cellNumber,
          contact_preference: phoneCheckBox,
          contact_preference_email: emailCheckbox,
          contact_preference_text: textCheckbox,
          text_consent: checkboxesVal,
        },
      }
      const response = await fetch(apiUrl("UpdateContactRecord"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      })
      catchdata = data
      if (response.ok) {
        // (original left this branch empty)
      }

      const result = await response.json()
      responseFromAPI = JSON.stringify(result)
    } catch (error) {
      console.error("Error:", error)
    }
  }

  async function addedConfigIdWithSomeDataOnLoad() {
__ea_trace("modules/shared/info-form.ts","addedConfigIdWithSomeDataOnLoad",[],[]);
    const dealId = storage.rawGet("dealId")
    let catchdata: any = ""
    try {
      const data = {
        properties: {
          deal_id: dealId,
          dealstage: "157471034",
        },
      }
      const response = await fetch(apiUrl("UpdateDealRecord"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      })
      catchdata = data
      if (response.ok) {
      }

      const result = await response.json()
      responseFromAPI = JSON.stringify(result)
    } catch (error) {
      console.error("Error:", error)
    }
    return true
  }

  q("#get-utility-info").addEventListener("click", validateForm) // run function click on get utility button
  q("#save-remind").addEventListener("click", saveMyPlace) // run function click on save my place button

  // check variation for the LMI page
  let utilityIdSession: any
  let LmiAvailability: any
  let LmiCensusAvailability: any
  const DisplayLmiElementList: any[] = []
  const requireLmiElementList: any[] = []
  const DisplayUsageElementList: any[] = []
  const requireUsageElementList: any[] = []
  const DisplayConsentElementList: any[] = []
  const requireConsentElementList: any[] = []
  let consetPaymentReq: any
  let LMICensusTractLevels: any
  void utilityIdSession
  void DisplayLmiElementList
  void requireLmiElementList

  let sampleSessiontype: any
  let sampleOriginType: any

  if (storage.rawGet("attcode") != null) {
    sampleOriginType = "Invite"
    sampleSessiontype = "Consumer Completing"
  } else {
    sampleOriginType = "Direct"
    sampleSessiontype = "Consumer Only"
  }

  let offerId: any = ""
  let lmiCensusTractLevel: any = ""

  async function updateDealStage(value: any) {
__ea_trace("modules/shared/info-form.ts","updateDealStage",[value],["value"]);
    let address1: any = qi("#address1").value.trim()
    let address2: any = qi("#address2").value.trim()
    let city: any = qi("#city").value.trim()
    const state = qi("#state").value
    let county: any = qi("#county").value
    if (county === "" || county == null || county === undefined) {
      county = ""
    }
    const code = qi("#code").value.trim()
    const dealId = storage.rawGet("dealId")
    let catchdata: any = ""
    try {
      const data = {
        properties: {
          deal_id: dealId,
          dealstage: value,
          product_offer_id: offerId,
          lmi_census_tract_level: lmiCensusTractLevel,
          street_address: address1,
          street_address_2: address2,
          city: city,
          service_state_abbreviation: state,
          postal_code: code,
          county: county,
        },
      }
      const response = await fetch(apiUrl("UpdateDealRecord"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      })
      catchdata = data
      if (response.ok) {
        if (
          address1 !== "" &&
          address1 != null &&
          address1 !== undefined &&
          address1 !== "null" &&
          address1 !== "undefined"
        ) {
          address1 = address1
        } else {
          address1 = ""
        }
        if (
          address2 !== "" &&
          address2 != null &&
          address2 !== undefined &&
          address2 !== "null" &&
          address2 !== "undefined"
        ) {
          address2 = `, ${address2}`
        } else {
          address2 = ""
        }
        if (city !== "" && city != null && city !== undefined && city !== "null" && city !== "undefined") {
          city = `, ${city}`
        } else {
          city = ""
        }
        storage.rawSet("Address", address1 + address2 + city)
        storage.rawSet("Street Address", address1 + address2)
      }

      const result = await response.json()
      responseFromAPI = JSON.stringify(result)
    } catch (error) {
      console.error("Error:", error)
    }
    return true
  }

  async function getAvailability() {
__ea_trace("modules/shared/info-form.ts","getAvailability",[],[]);
    const exampleUtilityId = storage.rawGet("utility_id")
    const response = await fetch(
      hubdbRowsUrl("utilityAvailability", {
        utility_id__eq: String(exampleUtilityId),
      }),
      { method: "GET" },
    )
    const data = await response.json()
    const rows = data.objects

    let IsUtilityIdMatched = false
    void IsUtilityIdMatched
    if (rows.length >= 1) {
      storage.rawSet("Session Config ID", rows[0].id)
      rows.forEach((element: any) => {
__ea_trace("modules/shared/info-form.ts","(anon)",[element],["element"]);
        if (exampleUtilityId === element.values["1"]) {
          storage.rawSet("stateForLMIConfig", element.values["13"].name)
          storage.rawSet("UtilitySessionDetails", JSON.stringify(element.values))
          if (element.values["24"] !== undefined) {
            storage.rawSet("credentialProvider", element.values["24"].name)
          }
          IsUtilityIdMatched = true

          utilityIdSession = element.values["1"]
          if (element.values["68"]) {
            LMICensusTractLevels = element.values["68"].name
          }

          if (element.values["30"] === "1") {
            LmiAvailability = "Yes"
          } else if (element.values["30"] === "0") {
            LmiAvailability = "No"
          } else {
            LmiAvailability = ""
          }
          if (element.values["31"] === "1") {
            LmiCensusAvailability = "Yes"
          } else if (element.values["31"] === "0") {
            LmiCensusAvailability = "No"
          } else {
            LmiCensusAvailability = ""
          }
          lmiFlow()
        } else {
          myInfocloseLoader()
        }
      })
    } else {
      myInfocloseLoader()
    }
  }
  async function getOfferIDFromFarmProductsNoLMI() {
__ea_trace("modules/shared/info-form.ts","getOfferIDFromFarmProductsNoLMI",[],[]);
    const farmId = storage.rawGet("FarmID")
    const OfferTypeVal = "Open"
    try {
      const response = await fetch(
        hubdbRowsUrl("farms", {
          farm_id: String(farmId),
          offer_type: OfferTypeVal,
        }),
        { method: "GET" },
      )
      const data = await response.json()
      const rows = data.objects
      if (rows.length >= 1) {
        offerId = rows[0].values[3]
        storage.rawSet("offerIdNoLMI", offerId)
      }
    } catch (error) {
      console.error("Error in Farm Product hubdb fetch block:", error)
    }
  }
  async function getOfferIDFromFarmProductsYesLMI() {
__ea_trace("modules/shared/info-form.ts","getOfferIDFromFarmProductsYesLMI",[],[]);
    const farmId = storage.rawGet("FarmID")
    const OfferTypeVal = "LMI"
    try {
      const response = await fetch(
        hubdbRowsUrl("farms", {
          farm_id: String(farmId),
          offer_type: OfferTypeVal,
        }),
        { method: "GET" },
      )
      const data = await response.json()
      const rows = data.objects
      if (rows.length >= 1) {
        offerId = rows[0].values[3]
        storage.rawSet("offerIdYesLMI", offerId)
      }
    } catch (error) {
      console.error("Error in Farm Products hubdb fetch block:", error)
    }
  }
  async function lmiFlow() {
__ea_trace("modules/shared/info-form.ts","lmiFlow",[],[]);
    await checkUsageConfigurationVariation()
    await checkConsentConfigurationVariation()
    if (LmiAvailability === "No") {
      await getOfferIDFromFarmProductsNoLMI()
      await updateDealStage("127892569")
      myInfocloseLoader()
      const cp = storage.rawGet("credentialProvider")
      if (cp === "NSE (Hansen)") {
        window.location.href = "/my-usage" // redirect to myusage page
      } else if (cp === "Bayou") {
        window.location.href = "/myusage" // redirect to myusage page
      } else {
        window.location.href = "/my-usage"
      }
    } else if (LmiAvailability === "Yes" && LmiCensusAvailability === "No") {
      await getOfferIDFromFarmProductsYesLMI()
      await getOfferIDFromFarmProductsNoLMI()
      await updateDealStage("127892566")
      exampleUtilityId = storage.rawGet("utility_id")
      const stateForLMIConfig = storage.rawGet("stateForLMIConfig")
      const responseLMIC = await fetch(
        hubdbRowsUrl("lmiConfig", {
          orderBy: "lmi_sequence",
          utility_id: exampleUtilityId,
          state: stateForLMIConfig as string,
        }),
        { method: "GET" },
      )
      const dataLMIC = await responseLMIC.json()
      const rowsLMIC = dataLMIC.objects
      dq("#Income_Exceed_Threshold").style.display = "none"
      dq("#Program_Participation").style.display = "none"
      dq("#Request_Income_Check").style.display = "none"
      dq("#Request_Income_Check .ipiaap-items-3").style.display = "none"
      dq("#Attestation").style.display = "none"
      dq("#Attestation .LMICheckCont").style.display = "none"
      dq("#Attestation .LMISignature").style.display = "none"
      dq("#Cencus_tract_map_upload").style.display = "none"
      dq("#Cencus_tract_map_upload .ipiaap-items-2").style.display = "none"
      dq("#Cencus_tract_map_upload #cencus-track-radio").style.display = "none"
      dq("#Cencus_tract_map_upload .LMICheckCont").style.display = "none"
      dq("#Cencus_tract_map_upload .LMISignature").style.display = "none"
      if (rowsLMIC.length >= 1) {
        rowsLMIC.forEach((element: any) => {
__ea_trace("modules/shared/info-form.ts","(anon)",[element],["element"]);
          const lmiOptAvl = element.values["5"].name
          const lmiOptType = element.values["6"].name
          const lmiSeq = element.values["4"]
          const lmiOptText = element.values["7"]
          const lmiInLineText = element.values["9"]
          const lmiCheckbox = element.values["12"].name
          const lmiSignature = element.values["16"].name
          const lmiCheckContent = element.values["13"]
          const fileUploadControl = element.values["14"]
          const lmiOptionLinkText = element.values["17"]
          let lmiOptionLink: any
          if (lmiOptionLinkText) {
            lmiOptionLink = element.values["8"]
          }
          if (lmiOptAvl === "Yes" && lmiOptType === "Threshold Opt-Out") {
            dq(".lmifieldswrapper .iethItem").setAttribute("order", lmiSeq)
            dq("#Income_Exceed_Threshold").style.display = "block"
            dq("#Income_Exceed_Threshold .lmiOptText").innerHTML = lmiOptText
            dq("#Income_Exceed_Threshold .lmiInLineTxt").innerHTML = lmiInLineText
            if (lmiOptionLink) {
              dq("#Income_Exceed_Threshold .lmiLinkTxt").innerHTML =
                `<a href="${lmiOptionLink}">${lmiOptionLinkText}</a>`
            }
          } else if (lmiOptAvl === "Yes" && lmiOptType === "Program") {
            dq(".lmifieldswrapper .ppItem").setAttribute("order", lmiSeq)
            dq("#Program_Participation").style.display = "block"
            dq("#Program_Participation .lmiOptText").innerHTML = lmiOptText
            dq("#Program_Participation .lmiInLineTxt").innerHTML = lmiInLineText
            if (lmiOptionLink) {
              dq("#Program_Participation .lmiLinkTxt").innerHTML = `<a href="${lmiOptionLink}">${lmiOptionLinkText}</a>`
            }
            const lmiProgOpts = element.values["10"]
            const lmiDocsOpts = element.values["11"]
            const lmiProgOptsarray = lmiProgOpts.split(",")
            const prgselectbx = dq("#Program_Participation #incomeThreshold-options") as unknown as HTMLSelectElement
            lmiProgOptsarray.forEach((item: any) => {
__ea_trace("modules/shared/info-form.ts","(anon)",[item],["item"]);
              const option = document.createElement("option")
              option.text = item
              prgselectbx.add(option)
            })

            const lmiDocsOptsitems = lmiDocsOpts.split("|")
            const lmiDocsOptsresult: any[] = []
            lmiDocsOptsitems.forEach((item: any) => {
__ea_trace("modules/shared/info-form.ts","(anon)",[item],["item"]);
              const parts = item.split(":")
              const key = parts[0]
              const value = parts[1].split(",")
              const obj: any = {}
              obj[key] = value
              lmiDocsOptsresult.push(obj)
              const prgselectbxoptionsAll = prgselectbx.querySelectorAll("option")
              prgselectbxoptionsAll.forEach((option) => {
__ea_trace("modules/shared/info-form.ts","(anon)",[option],["option"]);
                if (option.text === key) {
                  option.setAttribute("value", value as any)
                }
              })
            })
          } else if (lmiOptAvl === "Yes" && lmiOptType === "Income Verification") {
            dq(".lmifieldswrapper .rICItem").setAttribute("order", lmiSeq)
            dq("#Request_Income_Check").style.display = "block"
            dq("#Request_Income_Check .lmiOptText").innerHTML = lmiOptText
            dq("#Request_Income_Check .lmiInLineTxt").innerHTML = lmiInLineText
            if (lmiOptionLink) {
              dq("#Request_Income_Check .lmiLinkTxt").innerHTML = `<a href="${lmiOptionLink}">${lmiOptionLinkText}</a>`
            }
            incomefileUploadControl = fileUploadControl
          } else if (lmiOptAvl === "Yes" && lmiOptType === "Attestation") {
            dq(".lmifieldswrapper .attsItem").setAttribute("order", lmiSeq)
            dq("#Attestation").style.display = "block"
            dq("#Attestation .lmiOptText").innerHTML = lmiOptText
            dq("#Attestation .lmiInLineTxt").innerHTML = lmiInLineText
            if (lmiCheckbox === "Yes") {
              dq("#Attestation .LMICheckCont").style.display = "block"
              dq("#Attestation .LMICheckCont label span").innerHTML = lmiCheckContent
            }
            if (lmiSignature === "Yes") {
              dq("#Attestation .LMISignature").style.display = "block"
            }
            if (lmiOptionLink) {
              dq("#Attestation .lmiLinkTxt").innerHTML = `<a href="${lmiOptionLink}">${lmiOptionLinkText}</a>`
            }
          } else if (lmiOptAvl === "Yes" && lmiOptType === "Census Tract Map Upload") {
            dq(".lmifieldswrapper .ctmuItem").setAttribute("order", lmiSeq)
            dq("#Cencus_tract_map_upload").style.display = "block"
            dq("#Cencus_tract_map_upload .lmiOptText").innerHTML = lmiOptText
            dq("#Cencus_tract_map_upload .lmiInLineTxt").innerHTML = lmiInLineText
            if (lmiCheckbox === "Yes") {
              dq("#Cencus_tract_map_upload .LMICheckCont").style.display = "block"
              dq("#Cencus_tract_map_upload .LMICheckCont label span").innerHTML = lmiCheckContent
            }
            if (lmiSignature === "Yes") {
              dq("#Cencus_tract_map_upload .LMISignature").style.display = "block"
            }
            if (lmiOptionLink) {
              dq("#Cencus_tract_map_upload .lmiLinkTxt").innerHTML =
                `<a href="${lmiOptionLink}">${lmiOptionLinkText}</a>`
            }
            if (fileUploadControl.name === "Yes") {
              dq("#Cencus_tract_map_upload #cencus-track-radio").style.display = "block"
            }
          }
        })
      } else {
        const cp = storage.rawGet("credentialProvider")
        if (cp === "NSE (Hansen)") {
          window.location.href = "/my-usage" // redirect to myusage page
        } else if (cp === "Bayou") {
          window.location.href = "/myusage" // redirect to myusage page
        } else {
          window.location.href = "/my-usage"
        }
      }
      const lmifieldswrapperul = dq(".lmifieldswrapper")
      const lmifieldswrapperlistItems = Array.from(lmifieldswrapperul.querySelectorAll(".list"))
      lmifieldswrapperlistItems.sort((a, b) => (a.getAttribute("order") as any) - (b.getAttribute("order") as any))
      lmifieldswrapperul.innerHTML = ""
      lmifieldswrapperlistItems.forEach((item) => {
__ea_trace("modules/shared/info-form.ts","(anon)",[item],["item"]);
        lmifieldswrapperul.appendChild(item)
      })

      myInfocloseLoader()
      const mf = di("page_1_my_info_widget")
      const lmi = di("page_1_lmi_widget")
      mf.style.display = "none"
      window.scrollTo({
        top: 0,
        behavior: "smooth", // Smooth scroll
      })
      lmi.style.display = "block"
    } else if (LmiAvailability === "Yes" && LmiCensusAvailability === "Yes") {
      await getOfferIDFromFarmProductsYesLMI()
      await getOfferIDFromFarmProductsNoLMI()

      const hensonApiUrl = "https://hansen.danmarcrm.com/lmiHansen.php"
      const requestBody = {
        line1: qi("#address1").value.trim(),
        city: qi("#city").value.trim(),
        state: qi("#state").value,
        zip: qi("#code").value.trim(),
      }
      let catchdata: any = ""
      try {
        const response = await fetch(hensonApiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        })
        catchdata = requestBody
        const responseData = await response.json()
        responseFromAPI = JSON.stringify(responseData)

        if (responseData.result === "valid") {
          lmiCensusTractLevel = LMICensusTractLevels
          await updateDealStage("127892569")
          myInfocloseLoader()
          const cp = storage.rawGet("credentialProvider")
          if (cp === "NSE (Hansen)") {
            window.location.href = "/my-usage" // redirect to myusage page
          } else if (cp === "Bayou") {
            window.location.href = "/myusage" // redirect to myusage page
          } else {
            window.location.href = "/my-usage"
          }
        } else if (responseData.result === "notvalid") {
          lmiCensusTractLevel = LMICensusTractLevels
          await updateDealStage("127892566")
          exampleUtilityId = storage.rawGet("utility_id")
          const stateForLMIConfig = storage.rawGet("stateForLMIConfig")
          const responseLMIC = await fetch(
            hubdbRowsUrl("lmiConfig", {
              orderBy: "lmi_sequence",
              utility_id: exampleUtilityId,
              state: stateForLMIConfig as string,
            }),
            { method: "GET" },
          )
          const dataLMIC = await responseLMIC.json()
          const rowsLMIC = dataLMIC.objects
          dq("#Income_Exceed_Threshold").style.display = "none"
          dq("#Program_Participation").style.display = "none"
          dq("#Request_Income_Check").style.display = "none"
          dq("#Request_Income_Check .ipiaap-items-3").style.display = "none"
          dq("#Attestation").style.display = "none"
          dq("#Attestation .LMICheckCont").style.display = "none"
          dq("#Attestation .LMISignature").style.display = "none"
          dq("#Cencus_tract_map_upload").style.display = "none"
          dq("#Cencus_tract_map_upload .ipiaap-items-2").style.display = "none"
          dq("#Cencus_tract_map_upload #cencus-track-radio").style.display = "none"
          dq("#Cencus_tract_map_upload .LMICheckCont").style.display = "none"
          dq("#Cencus_tract_map_upload .LMISignature").style.display = "none"
          if (rowsLMIC.length >= 1) {
            rowsLMIC.forEach((element: any) => {
__ea_trace("modules/shared/info-form.ts","(anon)",[element],["element"]);
              const lmiOptAvl = element.values["5"].name
              const lmiOptType = element.values["6"].name
              const lmiSeq = element.values["4"]
              const lmiOptText = element.values["7"]
              const lmiInLineText = element.values["9"]
              const lmiCheckbox = element.values["12"].name
              const lmiSignature = element.values["16"].name
              const lmiCheckContent = element.values["13"]
              const fileUploadControl = element.values["14"]
              const lmiOptionLinkText = element.values["17"]
              let lmiOptionLink: any
              if (lmiOptionLinkText) {
                lmiOptionLink = element.values["8"]
              }
              if (lmiOptAvl === "Yes" && lmiOptType === "Threshold Opt-Out") {
                dq(".lmifieldswrapper .iethItem").setAttribute("order", lmiSeq)
                dq("#Income_Exceed_Threshold").style.display = "block"
                dq("#Income_Exceed_Threshold .lmiOptText").innerHTML = lmiOptText
                dq("#Income_Exceed_Threshold .lmiInLineTxt").innerHTML = lmiInLineText
                if (lmiOptionLink) {
                  dq("#Income_Exceed_Threshold .lmiLinkTxt").innerHTML =
                    `<a href="${lmiOptionLink}">${lmiOptionLinkText}</a>`
                }
              } else if (lmiOptAvl === "Yes" && lmiOptType === "Program") {
                dq(".lmifieldswrapper .ppItem").setAttribute("order", lmiSeq)
                dq("#Program_Participation").style.display = "block"
                dq("#Program_Participation .lmiOptText").innerHTML = lmiOptText
                dq("#Program_Participation .lmiInLineTxt").innerHTML = lmiInLineText
                if (lmiOptionLink) {
                  dq("#Program_Participation .lmiLinkTxt").innerHTML =
                    `<a href="${lmiOptionLink}">${lmiOptionLinkText}</a>`
                }
                const lmiProgOpts = element.values["10"]
                const lmiDocsOpts = element.values["11"]
                const lmiProgOptsarray = lmiProgOpts.split(",")
                const prgselectbx = dq(
                  "#Program_Participation #incomeThreshold-options",
                ) as unknown as HTMLSelectElement
                lmiProgOptsarray.forEach((item: any) => {
__ea_trace("modules/shared/info-form.ts","(anon)",[item],["item"]);
                  const option = document.createElement("option")
                  option.text = item
                  prgselectbx.add(option)
                })

                const lmiDocsOptsitems = lmiDocsOpts.split("|")
                const lmiDocsOptsresult: any[] = []
                lmiDocsOptsitems.forEach((item: any) => {
__ea_trace("modules/shared/info-form.ts","(anon)",[item],["item"]);
                  const parts = item.split(":")
                  const key = parts[0]
                  const value = parts[1].split(",")
                  const obj: any = {}
                  obj[key] = value
                  lmiDocsOptsresult.push(obj)
                  const prgselectbxoptionsAll = prgselectbx.querySelectorAll("option")
                  prgselectbxoptionsAll.forEach((option) => {
__ea_trace("modules/shared/info-form.ts","(anon)",[option],["option"]);
                    if (option.text === key) {
                      option.setAttribute("value", value as any)
                    }
                  })
                })
              } else if (lmiOptAvl === "Yes" && lmiOptType === "Income Verification") {
                dq(".lmifieldswrapper .rICItem").setAttribute("order", lmiSeq)
                dq("#Request_Income_Check").style.display = "block"
                dq("#Request_Income_Check .lmiOptText").innerHTML = lmiOptText
                dq("#Request_Income_Check .lmiInLineTxt").innerHTML = lmiInLineText
                if (lmiOptionLink) {
                  dq("#Request_Income_Check .lmiLinkTxt").innerHTML =
                    `<a href="${lmiOptionLink}">${lmiOptionLinkText}</a>`
                }
                incomefileUploadControl = fileUploadControl
              } else if (lmiOptAvl === "Yes" && lmiOptType === "Attestation") {
                dq(".lmifieldswrapper .attsItem").setAttribute("order", lmiSeq)
                dq("#Attestation").style.display = "block"
                dq("#Attestation .lmiOptText").innerHTML = lmiOptText
                dq("#Attestation .lmiInLineTxt").innerHTML = lmiInLineText
                if (lmiCheckbox === "Yes") {
                  dq("#Attestation .LMICheckCont").style.display = "block"
                  dq("#Attestation .LMICheckCont label span").innerHTML = lmiCheckContent
                }
                if (lmiSignature === "Yes") {
                  dq("#Attestation .LMISignature").style.display = "block"
                }
                if (lmiOptionLink) {
                  dq("#Attestation .lmiLinkTxt").innerHTML = `<a href="${lmiOptionLink}">${lmiOptionLinkText}</a>`
                }
              } else if (lmiOptAvl === "Yes" && lmiOptType === "Census Tract Map Upload") {
                dq(".lmifieldswrapper .ctmuItem").setAttribute("order", lmiSeq)
                dq("#Cencus_tract_map_upload").style.display = "block"
                dq("#Cencus_tract_map_upload .lmiOptText").innerHTML = lmiOptText
                dq("#Cencus_tract_map_upload .lmiInLineTxt").innerHTML = lmiInLineText
                if (lmiCheckbox === "Yes") {
                  dq("#Cencus_tract_map_upload .LMICheckCont").style.display = "block"
                  dq("#Cencus_tract_map_upload .LMICheckCont label span").innerHTML = lmiCheckContent
                }
                if (lmiSignature === "Yes") {
                  dq("#Cencus_tract_map_upload .LMISignature").style.display = "block"
                }
                if (lmiOptionLink) {
                  dq("#Cencus_tract_map_upload .lmiLinkTxt").innerHTML =
                    `<a href="${lmiOptionLink}">${lmiOptionLinkText}</a>`
                }
                if (fileUploadControl.name === "Yes") {
                  dq("#Cencus_tract_map_upload #cencus-track-radio").style.display = "block"
                }
              }
            })
          } else {
            const cp = storage.rawGet("credentialProvider")
            if (cp === "NSE (Hansen)") {
              window.location.href = "/my-usage" // redirect to myusage page
            } else if (cp === "Bayou") {
              window.location.href = "/myusage" // redirect to myusage page
            } else {
              window.location.href = "/my-usage"
            }
          }
          const lmifieldswrapperul = dq(".lmifieldswrapper")
          const lmifieldswrapperlistItems = Array.from(lmifieldswrapperul.querySelectorAll(".list"))
          lmifieldswrapperlistItems.sort((a, b) => (a.getAttribute("order") as any) - (b.getAttribute("order") as any))
          lmifieldswrapperul.innerHTML = ""
          lmifieldswrapperlistItems.forEach((item) => {
__ea_trace("modules/shared/info-form.ts","(anon)",[item],["item"]);
            lmifieldswrapperul.appendChild(item)
          })

          myInfocloseLoader()
          const mf = di("page_1_my_info_widget")
          const lmi = di("page_1_lmi_widget")
          mf.style.display = "none"
          window.scrollTo({
            top: 0,
            behavior: "smooth", // Smooth scroll
          })
          lmi.style.display = "block"
        } else {
          myInfocloseLoader()
          alert("Unexpected Error")
          location.reload()
        }
        if (response.status === 500 || response.status === 404) {
          const cp = storage.rawGet("credentialProvider")
          if (cp === "NSE (Hansen)") {
            window.location.href = "/my-usage" // redirect to myusage page
          } else if (cp === "Bayou") {
            window.location.href = "/myusage" // redirect to myusage page
          } else {
            window.location.href = "/my-usage"
          }
        }
      } catch (error) {
        console.error("Error calling API:", error)
        const cp = storage.rawGet("credentialProvider")
        if (cp === "NSE (Hansen)") {
          window.location.href = "/my-usage" // redirect to myusage page
        } else if (cp === "Bayou") {
          window.location.href = "/myusage" // redirect to myusage page
        } else {
          window.location.href = "/my-usage"
        }
      }
    }
  }
  async function checkUsageConfigurationVariation() {
__ea_trace("modules/shared/info-form.ts","checkUsageConfigurationVariation",[],[]);
    let usageAvaiablity: any
    let rows: any
    const SessionLocalStorage = await JSON.parse(storage.rawGet("UtilitySessionDetails") as string)

    if (SessionLocalStorage["32"] === 0) {
      usageAvaiablity = "No"
    } else if (SessionLocalStorage["32"] === 1) {
      usageAvaiablity = "Yes"
    } else {
      usageAvaiablity = ""
    }
    void usageAvaiablity

    try {
      const response = await fetch(
        hubdbRowsUrl("usageConfig", {
          usage_required_for_contract__eq: "Yes",
          origin_type__eq: sampleOriginType,
          session_type__eq: sampleSessiontype,
        }),
        { method: "GET" },
      )
      const data = await response.json()
      rows = data.objects

      if (rows.length >= 1) {
        if (rows[0].values["5"].name === "Yes") {
          DisplayUsageElementList.push("UTILITY CREDENTIALS DISPLAY")
        }
        if (rows[0].values["7"].name === "Yes") {
          DisplayUsageElementList.push("CREATE UTILITY ACCOUNT DISPLAY")
        }
        if (rows[0].values["9"].name === "Yes") {
          DisplayUsageElementList.push("SKIP FOR NOW DISPLAY")
        }
        if (rows[0].values["11"].name === "Yes") {
          DisplayUsageElementList.push("UTILITY ACCOUNT NUMBER DISPLAY")
        }
        if (rows[0].values["13"].name === "Yes") {
          DisplayUsageElementList.push("BILL UPLOAD DISPLAY")
        }

        if (rows[0].values["6"].name === "Yes") {
          requireUsageElementList.push("UTILITY CREDENTIALS REQUIRED")
        }
        if (rows[0].values["8"].name === "Yes") {
          requireUsageElementList.push("CREATE UTILITY ACCOUNT REQUIRED")
        }
        if (rows[0].values["10"].name === "Yes") {
          requireUsageElementList.push("SKIP FOR NOW REQUIRED")
        }
        if (rows[0].values["12"].name === "Yes") {
          requireUsageElementList.push("UTILITY ACCOUNT NUMBER REQUIRED")
        }
        if (rows[0].values["14"].name === "Yes") {
          requireUsageElementList.push("BILL UPLOAD REQUIRED")
        }

        storage.rawSet("usageDisplayelementList", JSON.stringify(DisplayUsageElementList))
        storage.rawSet("usagerequireElementList", JSON.stringify(requireUsageElementList))
        if (DisplayUsageElementList.length > 1) {
          // (original left this branch empty)
        } else {
          // (original left this branch empty)
        }
      } else {
        storage.rawSet("usageDisplayelementList", JSON.stringify(DisplayUsageElementList))
        storage.rawSet("usagerequireElementList", JSON.stringify(requireUsageElementList))
      }
    } catch (error) {
      console.error("Error in Usage hubdb fetch block:", error)
    }
  }
  async function checkConsentConfigurationVariation() {
__ea_trace("modules/shared/info-form.ts","checkConsentConfigurationVariation",[],[]);
    let rows: any
    const SessionLocalStorage = JSON.parse(storage.rawGet("UtilitySessionDetails") as string)
    if (SessionLocalStorage["33"] === 0) {
      consetPaymentReq = "No"
    } else if (SessionLocalStorage["33"] === 1) {
      consetPaymentReq = "Yes"
    } else {
      consetPaymentReq = "No"
    }
    try {
      const response = await fetch(
        hubdbRowsUrl("paymentConfig", {
          payment_required_for_billing__eq: consetPaymentReq,
          origin_type__eq: sampleOriginType,
          session_type__eq: sampleSessiontype,
        }),
        { method: "GET" },
      )
      const data = await response.json()
      rows = data.objects

      if (rows.length >= 1) {
        if (rows[0].values["5"].name === "Yes") {
          DisplayConsentElementList.push("PAYMENT OPTIONS DISPLAY")
        }
        if (rows[0].values["7"].name === "Yes") {
          DisplayConsentElementList.push("AUTHORIZATION CONSENT DISPLAY")
        }
        if (rows[0].values["9"].name === "Yes") {
          DisplayConsentElementList.push("CREDIT CHECK CONSENT DISPLAY")
        }
        if (rows[0].values["11"].name === "Yes") {
          DisplayConsentElementList.push("SUBSCRIPTION SIGNATURE DISPLAY")
        }
        if (rows[0].values["13"].name === "Yes") {
          DisplayConsentElementList.push("SUB ACKNOWLEDGMENT DISPLAY")
        }
        if (rows[0].values["15"].name === "Yes") {
          DisplayConsentElementList.push("DISCLOSURE SIGNATURE DISPLAY")
        }
        if (rows[0].values["17"].name === "Yes") {
          DisplayConsentElementList.push("DISC ACKNOWLEDGMENT DISPLAY")
        }
        if (rows[0].values["19"].name === "Yes") {
          DisplayConsentElementList.push("SUBSCRIPTION SIGNATURE SIGN DISPLAY")
        }
        if (rows[0].values["21"].name === "Yes") {
          DisplayConsentElementList.push("DISCLOSURE SIGNATURE SIGNED DISPLAY")
        }

        if (rows[0].values["6"].name === "Yes") {
          requireConsentElementList.push("PAYMENT OPTIONS REQUIRED")
        }
        if (rows[0].values["8"].name === "Yes") {
          requireConsentElementList.push("AUTHORIZATION CONSENT REQUIRED")
        }
        if (rows[0].values["10"].name === "Yes") {
          requireConsentElementList.push("CREDIT CHECK CONSENT REQUIRED")
        }
        if (rows[0].values["12"].name === "Yes") {
          requireConsentElementList.push("SUBSCRIPTION SIGNATURE REQUIRED")
        }
        if (rows[0].values["14"].name === "Yes") {
          requireConsentElementList.push("SUB ACKNOWLEDGMENT REQUIRED")
        }
        if (rows[0].values["16"].name === "Yes") {
          requireConsentElementList.push("DISCLOSURE SIGNATURE REQUIRED")
        }
        if (rows[0].values["18"].name === "Yes") {
          requireConsentElementList.push("DISC ACKNOWLEDGMENT REQUIRED")
        }
        if (rows[0].values["20"].name === "Yes") {
          requireConsentElementList.push("SUBSCRIPTION SIGNATURE SIGN REQUIRED")
        }
        if (rows[0].values["22"].name === "Yes") {
          requireConsentElementList.push("DISCLOSURE SIGNATURE SIGNED REQUIRED")
        }
        storage.rawSet("consentDisplayelementList", JSON.stringify(DisplayConsentElementList))
        storage.rawSet("consentrequireElementList", JSON.stringify(requireConsentElementList))
        if (DisplayConsentElementList.length > 1) {
          // (original left this branch empty)
        } else {
          // (original left this branch empty)
        }
      } else {
        storage.rawSet("consentDisplayelementList", JSON.stringify(DisplayConsentElementList))
        storage.rawSet("consentrequireElementList", JSON.stringify(requireConsentElementList))
      }
    } catch (error) {
      console.error("Error in Usage hubdb fetch block:", error)
    }
  }
  /* -------------------------------------- End Single enrollemnt code ------------------------------------- */
}
